import { companycamNotice, type Job } from "@roof-hawk/core";
import { useCallback, useState } from "react";

import { ErrorNote, Stub } from "../../components/ui";
import {
  type CompanycamLink,
  linkCompanycamJob,
  photosUrl,
} from "../../lib/companycam";
import { fmtDateTime } from "../../lib/format";
import { t } from "../../i18n";

/**
 * The Photos card: where this job's CompanyCam project is, or why it is not.
 *
 * This tab used to open app.companycam.com and nothing more — CompanyCam's
 * front door, with the rep left to find the right project themselves. Now the
 * job has a project of its own (ADR 0052) and this says which, whether we made
 * it or joined one already at the address, and — the part that matters when it
 * goes wrong — that it failed and is coming back.
 *
 * The button is the backstop under all of it. There is no scheduler behind this
 * integration by choice, so a rep who cannot wait for the next retry has
 * somewhere to press.
 */
export function CompanyCamCard({
  job,
  link,
  onLinked,
}: {
  job: Job;
  link: CompanycamLink | null;
  onLinked: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);

  const linked = Boolean(job.companycam_project_id);

  // What the last sync had to say about a project that is otherwise fine.
  // `last_error` on a linked row is the run's warnings, not a failure.
  const standing =
    link?.state === "linked" ? (link.last_error ?? "").trim() || null : null;

  const run = useCallback(async () => {
    setBusy(true);
    setError(null);
    setSaid(null);
    try {
      const outcome = await linkCompanycamJob(job.id);
      setSaid(
        outcome.source === "matched"
          ? t("Joined the project already at this address.")
          : t("Created the project in CompanyCam."),
      );
      // A warning is not a failure: the project exists, something around it
      // did not. Worth showing, because "the rep was not assigned" is
      // invisible otherwise.
      if (outcome.warnings?.length) {
        setError(outcome.warnings.join(" "));
      }
      await onLinked();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [job.id, onLinked]);

  return (
    <div className="block">
      <h2>{t("Photos")}</h2>

      <p className="muted" style={{ marginTop: 0 }}>
        {link
          ? t(companycamNotice(link.state, link.last_error))
          : linked
            ? t("Photos are in CompanyCam.")
            : t("This job has no CompanyCam project yet.")}
      </p>

      {/*
        A linked project's notice says "Photos are in CompanyCam." and stops,
        which is right for the ordinary case and wrong for the one that matters:
        a second project at the same address. Two projects means half the
        photographs are somewhere nobody is looking, so it is said here rather
        than folded into a sentence about everything being fine.
      */}
      {standing && (
        <p className="config-warn" role="status">
          {standing}
        </p>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <a
          className="btn-primary"
          href={photosUrl(job)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {linked ? t("Open this job in CompanyCam") : t("Open CompanyCam")}
        </a>
        {!linked && (
          <button
            className="btn-ghost"
            // Fire and forget: run() shows its own errors.
            onClick={() => void run()}
            disabled={busy}
          >
            {busy ? t("Working…") : t("Create / Link CompanyCam Project")}
          </button>
        )}
      </div>

      <ErrorNote error={error} />
      {said && (
        <p className="muted" style={{ marginTop: 10 }}>
          {said}
        </p>
      )}

      {linked ? (
        <p className="muted" style={{ marginTop: 14 }}>
          {job.companycam_link_source === "matched"
            ? t("Joined a project that was already at this address")
            : t("Created for this job")}
          {job.companycam_linked_at
            ? ` · ${fmtDateTime(job.companycam_linked_at)}`
            : ""}
        </p>
      ) : (
        <Stub
          title={t("No project yet")}
          message={t(
            "Every job gets a CompanyCam project as soon as it is created — or joins the one already at that address, so a repeat customer's photos stay together. If CompanyCam was unreachable, this comes back by itself; the button forces it.",
          )}
        />
      )}
    </div>
  );
}
